import {
  QBox,
  QButton,
  QCheckbox,
  QFormControl,
  QModal,
  QModalBody,
  QModalHeader,
  QStack,
  QText,
  useCurrentUser,
} from '@qualio/ui-components';
import { ChangeEvent, useCallback, useState } from 'react';
import { EventTemplateDetailsResponse } from '../../../../../../types';
import * as DisplayStrings from '../../../../../../displayStrings';
import { useExport } from '@qualio/export-components';
import { exportApi } from '../../../../../../api';
import { useTranslation } from 'react-i18next';
import { buildExportEventTemplatesPayload } from '../exportUtils';

type ExportEventTemplateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  template: EventTemplateDetailsResponse;
};

export const ExportEventTemplateModal = ({ isOpen, onClose, template }: ExportEventTemplateModalProps) => {
  const [coverPageEnabled, setCoverPageEnabled] = useState(true);
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { trigger: handleTriggerExport, isLoading: isExportLoading } = useExport(exportApi, {
    toastTitle: t('common.notifications.exportingEventCodeTitle', { code: template.prefix, title: template.name }),
  });

  const onCoverPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCoverPageEnabled(event.target.checked);
  };

  const onCloseCallback = useCallback(() => {
    setCoverPageEnabled(true);
    onClose();
  }, [onClose]);

  const handleExport = useCallback(() => {
    handleTriggerExport(
      buildExportEventTemplatesPayload(template, currentUser.companyId, currentUser.fullName, {
        coverPageEnabled,
      }),
    );
    onCloseCallback();
  }, [handleTriggerExport, template, currentUser, coverPageEnabled, onCloseCallback]);

  return (
    <QModal onClose={onCloseCallback} isOpen={isOpen} size="xl">
      <QModalHeader>
        <QText>{DisplayStrings.EventTemplateExportModalTitle}</QText>
      </QModalHeader>
      <QModalBody>
        <QStack spacing={6}>
          <QText fontSize="md">{DisplayStrings.EventTemplateExportModalSubText}</QText>
          <QFormControl
            label={DisplayStrings.EventTemplateExportCoverPageTitle}
            helper={DisplayStrings.EventTemplateExportCoverPageSubText}
          >
            <QCheckbox
              isChecked={coverPageEnabled}
              onChange={onCoverPageChange}
              data-cy="export-modal-coverpage-selection"
            >
              {DisplayStrings.EventTemplateExportIncludeCoverPage}
            </QCheckbox>
          </QFormControl>
        </QStack>
        <QBox mt={5} mb={3} display="flex" flexDirection="row" justifyContent="flex-end">
          <QBox mr={3}>
            <QButton variant="outline" onClick={onCloseCallback}>
              {DisplayStrings.Cancel}
            </QButton>
          </QBox>
          <QButton
            isLoading={isExportLoading}
            isDisabled={isExportLoading}
            onClick={handleExport}
            data-cy="export-event-template-modal-button"
          >
            {DisplayStrings.EventTemplateExport}
          </QButton>
        </QBox>
      </QModalBody>
    </QModal>
  );
};
